import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/cache/queryClient';
import {
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  getFolderRecipes,
  addRecipeToFolder,
  removeRecipeFromFolder,
  getRecipeFolders,
  updateRecipeFolders,
} from '@/services/api/folders';
import { useToast } from '@/context/ToastContext';

export function useFolders() {
  const query = useQuery({
    queryKey: queryKeys.folders,
    queryFn: getFolders,
  });

  // Separate system folders and user folders
  const systemFolders = query.data?.filter((f) => f.is_system) || [];
  const userFolders = query.data?.filter((f) => !f.is_system) || [];

  return {
    ...query,
    folders: query.data || [],
    systemFolders,
    userFolders,
  };
}

export function useFolderRecipes(folderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.folderRecipes(folderId || ''),
    queryFn: () => getFolderRecipes(folderId!),
    enabled: !!folderId && folderId !== 'all',
  });
}

export function useRecipeFolders(recipeId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.recipeFolders(recipeId || ''),
    queryFn: () => getRecipeFolders(recipeId!),
    enabled: !!recipeId,
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: createFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folders });
      success('Folder created');
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to create folder');
    },
  });
}

export function useUpdateFolder() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateFolder(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folders });
      success('Folder updated');
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to update folder');
    },
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: deleteFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folders });
      success('Folder deleted');
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to delete folder');
    },
  });
}

export function useAddRecipeToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, recipeId }: { folderId: string; recipeId: string }) =>
      addRecipeToFolder(folderId, recipeId),
    onSuccess: (_, { folderId, recipeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folderRecipes(folderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.recipeFolders(recipeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

export function useRemoveRecipeFromFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, recipeId }: { folderId: string; recipeId: string }) =>
      removeRecipeFromFolder(folderId, recipeId),
    onSuccess: (_, { folderId, recipeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folderRecipes(folderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.recipeFolders(recipeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

export function useUpdateRecipeFolders(recipeId: string) {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: (folderIds: string[]) => updateRecipeFolders(recipeId, folderIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipeFolders(recipeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.folders });
      success('Folders updated');
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to update folders');
    },
  });
}
